/**
 * Color intention that you want to used in your theme
 * @param theme Theme customization object
 */

import { lightBlue, purple, red } from "@mui/material/colors";

interface ThemeColors {
  darkPaper?: string;
  primaryLight?: string;
  primaryMain?: string;
  primaryDark?: string;
  primary200?: string;
  primary800?: string;
  primary900?: string;
  secondaryLight?: string;
  secondaryMain?: string;
  secondaryDark?: string;
  secondary200?: string;
  secondary800?: string;
  successLight?: string;
  successMain?: string;
  successDark?: string;
  success200?: string;
  success800?: string;
  warningLight?: string;
  warningMain?: string;
  warningSemiDark?: string;
  warningDark?: string;
  warning800?: string;
  errorLight?: string;
  errorMain?: string;
  errorDark?: string;
  error800?: string;
  grey50?: string;
  grey100?: string;
  grey300?: string;
  grey400?: string;
  grey700?: string;
  grey800?: string;
  orangeLight?: string;
  orangeMain?: string;
  orangeDark?: string;
  pinkLight?: string;
  pinkMain?: string;
  pinkDark?: string;
  tealLight?: string;
  tealMain?: string;
  tealDark?: string;
  lightGreenLight?: string;
  lightGreenMain?: string;
  lightGreenDark?: string;
  limeLight?: string;
  limeMain?: string;
  limeDark?: string;
  darkTextPrimary?: string;
  darkLevel1?: string;
  darkLevel2?: string;
  darkBackground?: string;
}

interface ThemeOption {
  colors?: ThemeColors;
  heading?: string;
  paper?: string;
  backgroundDefault?: string;
  background?: string;
  darkTextPrimary?: string;
  darkTextSecondary?: string;
  textDark?: string;
  menuSelected?: string;
  menuSelectedBack?: string;
  divider?: string;
  grey500?: string;
  customization?: {
    navType?: string;
    fontFamily?: string;
    borderRadius?: number;
  };
}

export default function themePalette(theme: ThemeOption) {
  return {
    mode: theme?.customization?.navType as "light" | "dark" | undefined,
    common: {
      black: theme.colors?.darkPaper,
    },
    primary: {
      light: theme.colors?.primaryLight,
      main: theme.colors?.primaryMain,
      dark: theme.colors?.primaryDark,
      200: theme.colors?.primary200,
      800: theme.colors?.primary800,
      900: theme.colors?.primary900,
    },
    secondary: {
      light: theme.colors?.secondaryLight,
      main: theme.colors?.secondaryMain,
      dark: theme.colors?.secondaryDark,
      200: theme.colors?.secondary200,
      800: theme.colors?.secondary800,
    },
    success: {
      light: theme.colors?.successLight,
      main: theme.colors?.successMain,
      dark: theme.colors?.successDark,
      200: theme.colors?.success200,
      800: theme.colors?.success800,
    },
    warning: {
      light: theme.colors?.warningLight,
      main: theme.colors?.warningMain,
      semiDark: theme.colors?.warningSemiDark,
      dark: theme.colors?.warningDark,
      800: theme.colors?.warning800,
    },
    error: {
      light: theme.colors?.errorLight,
      main: theme.colors?.errorMain,
      dark: theme.colors?.errorDark,
      700: "#d32f2f",
      800: theme.colors?.error800,
    },
    grey: {
      50: theme.colors?.grey50,
      100: theme.colors?.grey100,
      500: theme.darkTextSecondary,
      600: theme.heading,
      700: theme.darkTextPrimary,
      light: theme.colors?.grey100,
      main: theme.colors?.grey700,
      dark: theme.colors?.grey800,
      900: theme.textDark,
    },
    lightBlue: {
      light: lightBlue[300],
      main: lightBlue[500],
      dark: lightBlue[700],
    },
    orange: {
      light: theme.colors?.orangeLight,
      main: theme.colors?.orangeMain,
      dark: theme.colors?.orangeDark,
    },
    pink: {
      light: theme.colors?.pinkLight,
      main: theme.colors?.pinkMain,
      dark: theme.colors?.pinkDark,
    },
    teal: {
      light: theme.colors?.tealLight,
      main: theme.colors?.tealMain,
      dark: theme.colors?.tealDark,
    },
    lightGreen: {
      light: theme.colors?.lightGreenLight,
      main: theme.colors?.lightGreenMain,
      dark: theme.colors?.lightGreenDark,
    },
    lime: {
      light: theme.colors?.limeLight,
      main: theme.colors?.limeMain,
      dark: theme.colors?.limeDark,
    },
    purple: {
      light: purple[300],
      main: purple[500],
      dark: purple[700],
    },
    red: {
      light: red[300],
      main: red[500],
      dark: red[700],
    },
    dark: {
      light: theme.colors?.darkTextPrimary,
      main: theme.colors?.darkLevel1,
      dark: theme.colors?.darkLevel2,
      800: theme.colors?.darkBackground,
      900: theme.colors?.darkPaper,
    },
    text: {
      primary: theme.darkTextPrimary,
      secondary: theme.darkTextSecondary,
      dark: theme.textDark,
      hint: theme.colors?.grey100,
    },
    background: {
      paper: theme.paper,
      default: theme.backgroundDefault,
    },
  };
}
